import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getClients, createInvoice, getSectorConfig } from '../services/api';
import { showErrorToast, showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError } from '../utils/toastConfig';
import { FiArrowLeft, FiFileText, FiLogOut, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiMapPin, FiHash, FiBriefcase, FiCalendar, FiDollarSign, FiUsers, FiCreditCard } from 'react-icons/fi';
import './GenerateInvoice.css';

const DESCRIPTIONS = {
  'Education': 'Education Consultancy Services',
  'Information Technology': 'Information Technology and Consultancy Services',
};

const GenerateInvoice = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    amount: '',
    description: '',
  });
  const [sacCode, setSacCode] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 6;

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const { data } = await getClients();
        setClients(data);
      } catch (err) {
        console.error('Fetch clients error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  // Filter clients
  const filteredClients = clients.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.client_name.toLowerCase().includes(q) ||
      c.gst_number.toLowerCase().includes(q) ||
      c.state.toLowerCase().includes(q) ||
      c.sector.toLowerCase().includes(q)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);
  const indexOfLast = currentPage * clientsPerPage;
  const indexOfFirst = indexOfLast - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setInvoiceForm({
      invoice_date: new Date().toISOString().split('T')[0],
      amount: '',
      description: DESCRIPTIONS[client.sector] || '',
    });
    setShowPopup(true);
    setError('');
    // Fetch SAC code for this sector
    try {
      const { data } = await getSectorConfig(client.sector);
      setSacCode(data.sac_code || (client.sector === 'Education' ? '999293' : '998313'));
    } catch {
      setSacCode(client.sector === 'Education' ? '999293' : '998313');
    }
  };

  const handleFormChange = (e) => {
    setInvoiceForm({ ...invoiceForm, [e.target.name]: e.target.value });
  };

  const calculateTax = () => {
    const amount = parseFloat(invoiceForm.amount) || 0;
    const isSameState = selectedClient?.state?.toLowerCase() === 'telangana';
    if (isSameState) {
      const cgst = amount * 0.09;
      const sgst = amount * 0.09;
      return { cgst, sgst, igst: 0, total: amount + cgst + sgst, type: 'CGST+SGST' };
    } else {
      const igst = amount * 0.18;
      return { cgst: 0, sgst: 0, igst, total: amount + igst, type: 'IGST' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invoiceForm.amount || parseFloat(invoiceForm.amount) <= 0) {
      showErrorToast('Please enter a valid amount');
      setError('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    setError('');
    const toastId = showLoadingToast('Generating invoice...');

    try {
      const { data } = await createInvoice({
        client_id: selectedClient.id,
        invoice_date: invoiceForm.invoice_date,
        amount: invoiceForm.amount,
        description: invoiceForm.description,
      });
      updateLoadingToastSuccess(toastId, 'Invoice generated successfully!');
      setTimeout(() => {
        navigate(`/invoice/${data.id}`);
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to generate invoice';
      setError(errorMsg);
      updateLoadingToastError(toastId, errorMsg);
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    setTimeout(() => navigate('/login'), 500);
  };

  const tax = calculateTax();

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="gi-page">
      {/* Header */}
      <header className="gi-header">
        <div className="gi-header-left">
          <img src="/veramasalogo.png" className="logo" alt="logo" />
        </div>
        <div className="gi-header-right">
          <span className="gi-welcome">Welcome, {user?.username}</span>
          <button className="gi-btn-logout" onClick={handleLogout}>
            <FiLogOut /> Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="gi-main">
        {/* Top Bar: Back + Title */}
        <div className="gi-topbar">
          <button className="gi-back-btn" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>
        </div>

        {/* Client Selection Card */}
        <div className="gi-content-card">
          {/* Card Header */}
          <div className="gi-card-header">
            <div className="gi-card-header-left">
              <div className="gi-card-title-row">
                <h2>Select a Client</h2>
                {!loading && (
                  <span className="gi-count-badge">{filteredClients.length}</span>
                )}
              </div>
              <p className="gi-card-subtitle">Choose a client to generate a new invoice</p>
            </div>
            {!loading && clients.length > 0 && (
              <div className="gi-search-wrap">
                <FiSearch className="gi-search-icon" />
                <input
                  className="gi-search-input"
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="gi-search-clear" onClick={() => setSearchQuery('')}>
                    <FiX size={12} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Card Body */}
          <div className="gi-card-body">
            {loading ? (
              <div className="gi-state-msg">
                <div className="gi-state-spinner"></div>
                <p>Loading clients...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="gi-state-msg">
                <div className="gi-state-icon">
                  <FiFileText size={28} />
                </div>
                <h4>No clients found</h4>
                <p>Add a client first to start generating invoices.</p>
                <button className="gi-btn-primary" onClick={() => navigate('/add-client')}>
                  Add Client
                </button>
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="gi-state-msg">
                <div className="gi-state-icon">
                  <FiSearch size={28} />
                </div>
                <h4>No results found</h4>
                <p>No clients match "{searchQuery}". Try a different search.</p>
              </div>
            ) : (
              <div className="gi-clients-grid">
                {currentClients.map((client) => (
                  <div
                    key={client.id}
                    className="gi-card"
                    onClick={() => handleSelectClient(client)}
                  >
                    <div className="gi-card-top">
                      <div className="gi-card-avatar">
                        {client.client_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="gi-card-info">
                        <h3 className="gi-card-name">{client.client_name}</h3>
                        <p className="gi-card-addr">{client.address}</p>
                      </div>
                    </div>
                    <div className="gi-card-tags">
                      <span className="gi-tag gi-tag-gst">
                        <FiHash size={11} />
                        {client.gst_number}
                      </span>
                      <span className="gi-tag gi-tag-state">
                        <FiMapPin size={11} />
                        {client.state}
                      </span>
                      <span className="gi-tag gi-tag-sector">
                        <FiBriefcase size={11} />
                        {client.sector}
                      </span>
                    </div>
                    <div className="gi-card-hover-action">
                      <FiFileText size={16} />
                      <span>Generate Invoice</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Card Footer: Pagination */}
          {!loading && filteredClients.length > 0 && (
            <div className="gi-card-footer">
              <span className="gi-results-text">
                Showing {indexOfFirst + 1}–{Math.min(indexOfLast, filteredClients.length)} of {filteredClients.length} clients
              </span>
              {totalPages > 1 && (
                <div className="gi-pagination">
                  <button
                    className="gi-pg-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  {getPageNumbers().map((num) => (
                    <button
                      key={num}
                      className={`gi-pg-num ${currentPage === num ? 'gi-pg-active' : ''}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="gi-pg-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Invoice Form Popup */}
      {showPopup && selectedClient && (
        <div className="gi-overlay" onClick={() => setShowPopup(false)}>
          <div className="gi-popup" onClick={(e) => e.stopPropagation()}>
            {/* Popup Header */}
            <div className="gi-popup-header">
              <div>
                <h3>Generate Invoice</h3>
                <p className="gi-popup-client">For <strong>{selectedClient.client_name}</strong></p>
              </div>
              <button className="gi-popup-close" onClick={() => setShowPopup(false)}>
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="gi-error-msg">{error}</div>}

              {/* ── CLIENT DETAILS ── */}
              <div className="gi-section">
                <div className="gi-section-title">
                  <FiUsers size={13} /> Client Details
                </div>
                <div className="gi-prefilled-grid">
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">Customer Name</span>
                    <span className="gi-prefilled-value">{selectedClient.client_name}</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">GST No</span>
                    <span className="gi-prefilled-value">{selectedClient.gst_number}</span>
                  </div>
                  <div className="gi-prefilled-field gi-col-span-2">
                    <span className="gi-prefilled-label">Address</span>
                    <span className="gi-prefilled-value">{selectedClient.address}</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">Sector</span>
                    <span className="gi-prefilled-value">{selectedClient.sector}</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">Description</span>
                    <span className="gi-prefilled-value">{invoiceForm.description}</span>
                  </div>
                </div>
              </div>

              {/* ── YOUR DETAILS ── */}
              <div className="gi-section">
                <div className="gi-section-title">
                  <FiCreditCard size={13} /> Your Details
                </div>
                <div className="gi-prefilled-grid">
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">SAC Code</span>
                    <span className="gi-prefilled-value">{sacCode || '—'}</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">GST No</span>
                    <span className="gi-prefilled-value">36AAECV8279M1ZG</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">PAN No</span>
                    <span className="gi-prefilled-value">AAECV8279M</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">PO No</span>
                    <span className="gi-prefilled-value">NIL</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">PO Date</span>
                    <span className="gi-prefilled-value">NIL</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">Bank Name</span>
                    <span className="gi-prefilled-value">AXIS BANK</span>
                  </div>
                  <div className="gi-prefilled-field gi-col-span-2">
                    <span className="gi-prefilled-label">Bank Address</span>
                    <span className="gi-prefilled-value">DD COLONY Hyderabad</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">IFSC Code</span>
                    <span className="gi-prefilled-value">UTIB0004188</span>
                  </div>
                  <div className="gi-prefilled-field">
                    <span className="gi-prefilled-label">Account Number</span>
                    <span className="gi-prefilled-value">920020047084756</span>
                  </div>
                </div>
              </div>

              {/* ── INVOICE DETAILS (user fills) ── */}
              <div className="gi-section">
                <div className="gi-section-title">
                  <FiCalendar size={13} /> Invoice Details
                </div>
                <div className="gi-form-grid">
                  <div className="gi-form-group">
                    <label><FiCalendar size={13} /> Invoice Date *</label>
                    <input
                      type="date"
                      name="invoice_date"
                      value={invoiceForm.invoice_date}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="gi-form-group">
                    <label><FiDollarSign size={13} /> Amount (&#8377;) *</label>
                    <input
                      type="number"
                      name="amount"
                      value={invoiceForm.amount}
                      onChange={handleFormChange}
                      placeholder="Enter amount"
                      min="1"
                      step="0.01"
                      required
                    />
                  </div>
                </div>
              </div>

              {invoiceForm.amount && parseFloat(invoiceForm.amount) > 0 && (
                <div className="gi-tax-preview">
                  <div className="gi-tax-header">
                    <h4>Tax Breakdown</h4>
                    <span className={`gi-tax-type-badge ${tax.type === 'IGST' ? 'badge-igst' : 'badge-cgst'}`}>
                      {tax.type}
                    </span>
                  </div>
                  <div className="gi-tax-row">
                    <span>Base Amount</span>
                    <span>&#8377;{parseFloat(invoiceForm.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  {tax.type === 'CGST+SGST' ? (
                    <>
                      <div className="gi-tax-row">
                        <span>CGST (9%)</span>
                        <span>&#8377;{tax.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="gi-tax-row">
                        <span>SGST (9%)</span>
                        <span>&#8377;{tax.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <div className="gi-tax-row">
                      <span>IGST (18%)</span>
                      <span>&#8377;{tax.igst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="gi-tax-row gi-tax-total">
                    <span>Total Amount</span>
                    <span>&#8377;{tax.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <p className="gi-tax-note">
                    {selectedClient.state.toLowerCase() === 'telangana'
                      ? 'Intra-state supply: CGST + SGST applicable'
                      : 'Inter-state supply: IGST applicable'}
                  </p>
                </div>
              )}

              <div className="gi-popup-actions">
                <button type="button" className="gi-btn-cancel" onClick={() => setShowPopup(false)}>
                  Cancel
                </button>
                <button type="submit" className="gi-btn-submit" disabled={submitting}>
                  {submitting ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateInvoice;
